// frontend/src/features/drivers/DriverForm.js
// This React component renders the driver creation form, shows the agency’s
// active-driver count/limit, and calls onSave with a new driver entity.
// It does not place drivers into Active/Reserved lanes.
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectLimitByAgency } from '../agencies/agenciesSlice';
import { selectDriversByStatus } from './driverSlice'; // count from drivers slice

export default function DriverForm({
  onClose,
  onSave,
  agencyId,
}) {
  const [name, setName] = useState('');
  const [vehicleRegistrationNumber, setVehicleRegistrationNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);

  // Limit from agencies slice
  const limit = useSelector(state => selectLimitByAgency(state, agencyId));

  // Live count from drivers slice (assumes API is scoped to this agency)
  const activeDrivers = useSelector(selectDriversByStatus('Active'));
  const count = activeDrivers.length;

  const isAtLimit = count >= limit;

  const handleSubmit = async e => {
    e.preventDefault();
    const newDriver = {
      name,
      vehicle_registration_number: vehicleRegistrationNumber,
      phone,
      email,
    };

    try {
      await onSave(newDriver);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error adding driver');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md">
      <div className="flex justify-end">
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
          &times;
        </button>
      </div>
      <h2 className="text-xl font-semibold mb-4 text-center">
        Add Driver
      </h2>

      <div className="mb-4 text-sm text-yellow-800 bg-yellow-100 p-2 rounded">
        Active drivers: {count} / {limit}
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-100 p-3 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="John Doe"
          />
        </div>

        <div>
          <label htmlFor="vrn" className="block text-gray-700 mb-1">
            Vehicle Registration <span className="text-red-500">*</span>
          </label>
          <input
            id="vrn"
            type="text"
            required
            value={vehicleRegistrationNumber}
            onChange={e => setVehicleRegistrationNumber(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="ABC-1234"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-gray-700 mb-1">
            Phone
          </label>
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-gray-700 mb-1">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="driver@example.com"
          />
        </div>

        <button
          type="submit"
          disabled={isAtLimit}
          className={`w-full py-2 text-white rounded transition ${
            isAtLimit ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary/90'
          }`}
        >
          {isAtLimit ? 'Limit Reached' : 'Add Driver'}
        </button>
      </form>
    </div>
  );
}
